import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface AuthenticatedPunchCard {
  id: string;
  name: string;
  remaining_clips: number;
  expires_at?: string | null;
  valid_for_service: boolean;
  appointment_types?: unknown;
}

export interface AuthenticatedPunchCardsResponse {
  has_punch_cards: boolean;
  punch_cards: AuthenticatedPunchCard[];
}

interface ApplicablePunchCardRow {
  id: string;
  name: string;
  remaining_clips: number;
  expires_at?: string | null;
  appointment_types?: unknown;
}

function emptyPunchCards(): AuthenticatedPunchCardsResponse {
  return { has_punch_cards: false, punch_cards: [] };
}

function mapApplicableRows(
  rows: ApplicablePunchCardRow[] | null | undefined,
): AuthenticatedPunchCardsResponse {
  const punch_cards = (rows ?? []).map((card) => ({
    id: card.id,
    name: card.name,
    remaining_clips: card.remaining_clips,
    expires_at: card.expires_at ?? null,
    valid_for_service: true,
    appointment_types: card.appointment_types,
  }));
  return { has_punch_cards: punch_cards.length > 0, punch_cards };
}

/**
 * List punch cards the signed-in portal patient can use for this clinic/service.
 * Tries the booking edge function first, then the same canonical RPC staff
 * eligibility uses (`list_my_applicable_punch_cards`).
 */
export async function fetchAuthenticatedPunchCardsForBooking(input: {
  clinicSlug: string;
  serviceId: string;
  clinicId?: string | null;
}): Promise<AuthenticatedPunchCardsResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    return emptyPunchCards();
  }

  const { data, error } = await supabase.functions.invoke<{
    ok: true;
    data: AuthenticatedPunchCardsResponse;
  }>('portal-get-punch-cards-for-booking', {
    method: 'POST',
    body: {
      clinic_slug: input.clinicSlug,
      service_id: input.serviceId,
    },
  });

  if (!error && data?.ok) {
    return data.data;
  }

  if (input.clinicId) {
    const { data: rows, error: rpcError } = await supabase.rpc('list_my_applicable_punch_cards', {
      p_clinic_id: input.clinicId,
      p_service_id: input.serviceId,
    });
    if (!rpcError) {
      return mapApplicableRows(rows as ApplicablePunchCardRow[] | null);
    }
  }

  return emptyPunchCards();
}

/**
 * Fetch punch cards for authenticated portal users during booking.
 * Enabled whenever a session exists — do not wait for a local patient-row lookup.
 */
export function useAuthenticatedPunchCardsForBooking(
  clinicSlug: string | undefined,
  serviceId: string | null,
  enabled: boolean = true,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: ['authenticated-punch-cards-booking', clinicSlug, serviceId, clinicId] as const,
    enabled: enabled && Boolean(clinicSlug) && Boolean(serviceId),
    queryFn: async (): Promise<AuthenticatedPunchCardsResponse> => {
      if (!clinicSlug || !serviceId) {
        return emptyPunchCards();
      }
      return fetchAuthenticatedPunchCardsForBooking({
        clinicSlug,
        serviceId,
        clinicId,
      });
    },
    staleTime: 30_000,
    retry: false,
  });
}
